import { FormLeavingListener } from '@exam-center/components';
import {
  ExamLocationVerdict,
  ExamLocationRequestWorkflowStepCode,
  useSubmitReviewExamRoomRequestInfoMutation,
  useCreateOrUpdateExamLocationRequestVerdictMutation,
  SearchExamRoomRequestInfosDocument,
  AdminGetExamRoomRequestInfoByIdDocument,
  GetExamRoomRequestInfoByIdDocument,
  AdminSearchMyExamRoomRequestInfosDocument,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate } from '@exam-center/router';
import {
  PaperSection,
  Flex,
  Divider,
  Button,
  Form,
  ConfirmationModal,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission, WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo, useRef } from 'react';

import { ExamCenterLocationRequestVerdictFormField } from '../../components';
import { type AdminExamRoomRequestInfo, useAdminExamRoomRequestInfo } from '../../providers';
import {
  ExamCenterLocationRequestVerdictFormProvider,
  type ExamCenterLocationRequestVerdictFormType,
  useExamCenterLocationRequestVerdictFormContext,
} from '../../providers/exam-center-location-request-verdict-form-provider';
import { AdminManageExamRoomRequestRightsModal } from '../admin-manage-exam-room-request-rights-modal';
import { ExamCenterLocationRequestVerdictHistory } from '../exam-location-request-verdict-history';

interface ExamCenterLocationRequestVerdictProps {
  examCenterLocationRequest: AdminExamRoomRequestInfo;
}

function ExamCenterLocationRequestVerdict(props: Readonly<ExamCenterLocationRequestVerdictProps>) {
  const { examCenterLocationRequest } = props;
  const { showSnackbar } = useSnackbar();
  const formContext = useExamCenterLocationRequestVerdictFormContext();

  const navigate = useNavigate();
  const isSubmitFlowRef = useRef(false);

  const [isConfirmationModalOpen, { open, close }] = useDisclosure();

  const hasUpdatePermission = useHasPermission(
    [
      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.UPDATE,
      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW
        .UPDATE,
    ],
    'OR',
  );

  const [submitExamCenterLocationRequestVerdictMutation, { loading: isSubmitting }] =
    useSubmitReviewExamRoomRequestInfoMutation({
      onCompleted: () => {
        isSubmitFlowRef.current = false;
        showSnackbar({
          message: 'ยืนยันการอนุมัติคำขอเรียบร้อยแล้ว',
          variant: 'success',
        });
        navigate('/admin/exam-location/requests');
      },
      onError: () => {
        isSubmitFlowRef.current = false;
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการยืนยันการอนุมัติคำขอ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        AdminGetExamRoomRequestInfoByIdDocument,
        GetExamRoomRequestInfoByIdDocument,
        SearchExamRoomRequestInfosDocument,
        AdminSearchMyExamRoomRequestInfosDocument,
      ],
    });

  const [
    createOrUpdateExamCenterLocationRequestVerdictMutation,
    { loading: isCreatingOrUpdating },
  ] = useCreateOrUpdateExamLocationRequestVerdictMutation({
    onCompleted: () => {
      if (!isSubmitFlowRef.current) {
        showSnackbar({
          message: 'บันทึกผลการอนุมัติคำขอสำเร็จ',
          variant: 'success',
        });
      }
    },
    onError: () => {
      showSnackbar({
        message: 'เกิดข้อผิดพลาดในการบันทึกผลการอนุมัติคำขอ',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [AdminGetExamRoomRequestInfoByIdDocument, GetExamRoomRequestInfoByIdDocument],
  });

  const handleSave = async (data: ExamCenterLocationRequestVerdictFormType) => {
    await createOrUpdateExamCenterLocationRequestVerdictMutation({
      variables: {
        createOrUpdateExamLocationRequestVerdictInput: {
          approvalType: data.approvalType,
          grantedRoomId: data.grantedRoomId,
          verdict: data.verdict,
          comment: data.comment,
          examRoomRequestInfoId: examCenterLocationRequest.id,
        },
      },
    });
  };

  const handleSubmit = async () => {
    formContext.setValue('action', 'submit');
    const isValid = await formContext.trigger();

    if (isValid) {
      handleConfirmSubmit();
    } else {
      close();
    }
  };

  const handleConfirmSubmit = async () => {
    isSubmitFlowRef.current = true;
    await formContext.handleSubmit(handleSave)();
    await submitExamCenterLocationRequestVerdictMutation({
      variables: {
        submitReviewExamRoomRequestInfoInput: {
          examRoomRequestInfoId: examCenterLocationRequest.id,
        },
      },
    });
  };

  const isInReviewStep =
    examCenterLocationRequest.step &&
    [
      ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1,
      ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2,
    ].includes(examCenterLocationRequest.step);

  const verdictResult = formContext.watch('verdict');

  useEffect(() => {
    if (verdictResult === ExamLocationVerdict.Rejected) {
      const currentApprovalType = formContext.getValues('approvalType');
      const currentGrantedRoomId = formContext.getValues('grantedRoomId');

      if (currentApprovalType != null) {
        formContext.setValue('approvalType', undefined, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
      if (currentGrantedRoomId != null) {
        formContext.setValue('grantedRoomId', undefined, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    }
  }, [verdictResult, formContext]);

  const submittedVerdicts = useMemo(
    () =>
      examCenterLocationRequest.examLocationRequestVerdicts.filter(
        (verdict) => verdict.submittedAt !== null,
      ),
    [examCenterLocationRequest.examLocationRequestVerdicts],
  );

  return (
    <Form onSubmit={formContext.handleSubmit(handleSave)}>
      <Flex
        sx={(theme) => ({
          flex: 1,
          gap: theme.spacing(6),
          flexDirection: 'column',
        })}
      >
        {submittedVerdicts.length > 0 ? (
          <ExamCenterLocationRequestVerdictHistory
            verdicts={submittedVerdicts.map((verdict) => ({
              id: verdict.id,
              requestedAt: verdict.requestedAt,
              comment: verdict.comment,
              submittedAt: verdict.submittedAt,
              step: verdict.step,
              verdict: verdict.verdict ?? ExamLocationVerdict.Rejected,
              updatedUser: verdict.updatedUser,
              approvalType: verdict.approvalType,
            }))}
          />
        ) : null}
        {isInReviewStep && hasUpdatePermission ? (
          <>
            <PaperSection title='การอนุมัติคำขอ'>
              <Flex sx={{ height: '100%', flexDirection: 'column' }}>
                <Flex
                  sx={{
                    p: (theme) => theme.spacing(6),
                    height: '100%',
                    flexDirection: 'column',
                    gap: (theme) => theme.spacing(6),
                  }}
                >
                  <ExamCenterLocationRequestVerdictFormField isReadOnly={!hasUpdatePermission} />
                  {verdictResult === ExamLocationVerdict.Approved ? (
                    <AdminManageExamRoomRequestRightsModal />
                  ) : null}
                </Flex>
                <Divider />
                <WithPermission
                  requiredPermission={[
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                      .EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.UPDATE,
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                      .EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW.UPDATE,
                  ]}
                  options={{
                    operator: 'OR',
                  }}
                >
                  <Flex
                    sx={{
                      p: (theme) => theme.spacing(4),
                      height: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      type='submit'
                      variant='outlined'
                      color='secondary'
                      loading={isCreatingOrUpdating && !isSubmitFlowRef.current}
                    >
                      บันทึกผลการอนุมัติ
                    </Button>
                  </Flex>
                </WithPermission>
              </Flex>
            </PaperSection>
            <WithPermission
              requiredPermission={[
                APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
                APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                  .EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.UPDATE,
                APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                  .EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW.UPDATE,
              ]}
              options={{
                operator: 'OR',
              }}
            >
              <Button onClick={() => open()} size='large' sx={{ ml: 'auto', display: 'block' }}>
                ยืนยันการอนุมัติคำขอ
              </Button>
            </WithPermission>
            <ConfirmationModal
              open={isConfirmationModalOpen}
              loading={isCreatingOrUpdating || isSubmitting}
              title='ยืนยันการอนุมัติคำขอ'
              content={
                <Text variant='body1'>
                  คุณกำลังจะ
                  <Text component='span' variant='body1Semibold'>
                    {verdictResult === ExamLocationVerdict.Approved
                      ? 'อนุมัติคำขอ'
                      : 'ไม่อนุมัติคำขอ'}
                  </Text>
                  รายการนี้ ผลอนุมัติจะถือเป็นที่สิ้นสุด และไม่สามารถเปลี่ยนแปลงได้
                  กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
                </Text>
              }
              discardText='ยกเลิก'
              acceptText='ยืนยัน'
              onAccept={handleSubmit}
              onClose={close}
              onDiscard={close}
            />
          </>
        ) : null}
      </Flex>
      {hasUpdatePermission ? <FormLeavingListener onSubmit={handleSave} /> : null}
    </Form>
  );
}

export default function ExamCenterLocationRequestVerdictSelection() {
  const { examRoomRequestInfo } = useAdminExamRoomRequestInfo();

  return (
    <ExamCenterLocationRequestVerdictFormProvider
      initialValues={{
        verdicts: examRoomRequestInfo.examLocationRequestVerdicts.map((verdict) => ({
          id: verdict.id,
          verdict: verdict.verdict,
          comment: verdict.comment ?? '',
          approvalType: verdict.approvalType,
          grantedRoomId: verdict.grantedRoomId,
          submittedAt: verdict.submittedAt,
        })),
      }}
    >
      <ExamCenterLocationRequestVerdict examCenterLocationRequest={examRoomRequestInfo} />
    </ExamCenterLocationRequestVerdictFormProvider>
  );
}
